<template>
    <vue-select
        v-model="recipients"
        :getOptionLabel="recipientLabel"
        :placeholder="placeholder"
        v-validate="required ? 'required' : null"
        :class="{
            'border-danger': required ? errors.has('recipients') : false,
            'show-actions': !required ? true : false
        }"
        class="style-chooser"
        name="recipients"
        :options="recipientOptions"
        :multiple="multiple"
        @input="handleChanges"
        @search="onSearch"
    >
        <template v-slot:no-options="{ search }">
            <div class="pl-1">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isSearching">
                    Searching...
                </span>
                <span v-else>
                    No result
                </span>
            </div>
        </template>
    </vue-select>
</template>
<script type="text/javascript">
import Account from "@/services/account";
import Client from "@/services/client";
import ClientDocument from "@/services/client-documents";
import { debounce } from "lodash";

export default {
    components: {
        vueSelect: () => import("vue-select")
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        client: {
            type: Object,
            default: null
        },
        contact: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: "Client or Contact Email (required)"
        },
        required: {
            type: Boolean,
            default: true
        },
        multiple: {
            type: Boolean,
            default: true
        },
        labelWithEmail: {
            type: Boolean,
            default: true
        },
        /**
         * This is enhancement from VET-2728 that the client and contact will has same key "actor"
         */
        isContactClientSameKey: {
            type: Boolean,
            default: false
        },
        /**
         * Filter data false will populate all recipients data
         */

        filterData: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            recipients: [],
            recipientList: [],
            isSearching: false
        };
    },
    computed: {
        recipientOptions() {
            if (this.multiple) {
                let list = this.recipientList.filter(opt => {
                    let val = this.recipients.findIndex(r => {
                        return r.to_id == opt.to_id;
                    });

                    return val == -1;
                });
                return list;
            } else {
                return this.recipientList;
            }
        }
    },
    watch: {
        recipients() {
            this.recipientList = [];
        }
    },
    mounted() {
        this.setReceiver();
    },
    methods: {
        onSearch(query) {
            if (query.length > 0) {
                this.isSearching = true;
                this.search(query, this);
            }
        },
        search: debounce(async (query, self) => {
            const res = await ClientDocument.searchClientContact({
                query: query,
                field: "email"
            });

            self.isSearching = false;

            if (
                res.data &&
                res.data.response &&
                res.data.response.message &&
                res.data.response.message.code === 200
            ) {
                self.recipientList = self.processRecepientList(
                    res.data.response.resource
                );
            } else {
                self.recipientList = [];
            }
        }, 350),
        async fetchContacts() {
            let businessContacts = await Account.getCompanyContacts();
            businessContacts = businessContacts.resources;
            let clientContacts = await Client.getAllClientContact("email");
            clientContacts = clientContacts.resource;

            if (businessContacts && clientContacts) {
                this.recipientList = this.processRecepientList(
                    clientContacts,
                    businessContacts
                );
            }
        },
        processRecepientList(resources = []) {
            let arr = [];
            let ids = [];
            let emails = [];

            let oriId = null;
            for (const record of resources) {
                // skip duplicate id
                // this is important as the component might be unresponsive
                oriId = record.id;
                if (ids.includes(record.id) && emails.includes(record.email)) {
                    continue;
                } else {
                    ids.push(record.id);
                    emails.push(record.email);

                    record.id = `${record.id}_${record.email}`;
                }

                let to_id = "";
                let toIdArray = null; // Is the new structure of recipient to be send to the API, with array, BE no longer need to separate the string to type and id, BE will just can use toidarray[0] as type, toidarray[1] as the id
                let key = "a";
                switch (record.contact_type) {
                    case "contact":
                        to_id = `[actor]_${record.id}`;
                        key = "[actor]";
                        break;

                    case "business":
                    case "company":
                        to_id = `[business]_${record.id}`;
                        key = "business";
                        break;

                    default:
                        if (this.isContactClientSameKey) {
                            // use actor but without the []
                            // currently used in send file soap
                            key = "actor";
                        }
                        to_id = `a_${record.id}`;

                        break;
                }

                toIdArray = [key, oriId, record.email];
                arr.push({
                    ...record,
                    to_id,
                    toIdArray,
                    oriId
                });
            }

            return arr;
        },
        setReceiver() {
            let client = this.client;
            let contact = this.contact;

            if (client && client.email && client.email !== "") {
                let toIdKeyArray = "a";
                let toId = `a_${client.id}_${client.email}`;
                let contactType = "client";
                if (client.recipient_type == 1) {
                    toId = `[business]_${client.id}_${client.email}`;
                    toIdKeyArray = "business";
                    contactType = "business";
                } else if (client.recipient_is_client !== undefined) {
                    if (client.recipient_is_client) {
                        toId = `a_${client.id}_${client.email}`;
                        toIdKeyArray = "a";
                    } else {
                        toId = `[actor]_${client.id}_${client.email}`;
                        toIdKeyArray = "actor";
                    }
                }

                if (this.isContactClientSameKey) {
                    if (toIdKeyArray === "a") {
                        toIdKeyArray = "actor";
                    }
                    if (toId.startsWith("a_")) {
                        toId.replace("a_", "[actor]");
                    }
                }
                let toIdArray = [toIdKeyArray, client.id, client.email];

                let firstLast = client.firstname
                    ? client.firstname + " " + client.lastname
                    : null;
                let recipient = {
                    label: firstLast ? firstLast : client.name,
                    fullname: firstLast ? firstLast : client.name,
                    firstname: client.firstname ? client.firstname : null,
                    lastname: client.lastname ? client.lastname : null,
                    email: client.email,
                    contact_type: contactType,
                    actor_id: client.id,
                    to_id: toId,
                    toIdArray: toIdArray,
                    oriId: client.id
                };
                if (!this.filterData) {
                    recipient = {
                        ...recipient,
                        ...client
                    };
                }
                this.recipients.push(recipient);
            } else if (contact && contact.email && contact.email !== "") {
                let isCompany = this.$store.state.contact.isCompany;
                if (isCompany) {
                    let toId = `[business]_${contact.id}_${contact.email}`;
                    this.recipients.push({
                        label: contact.name,
                        email: contact.email,
                        contact_type: "business",
                        actor_id: contact.id,
                        to_id: toId,
                        toIdArray: ["business", contact.id, contact.email],
                        oriId: contact.id
                    });
                } else {
                    let toId = `[actor]_${contact.id}_${contact.email}`;
                    let fullname = contact.firstname
                        ? contact.firstname + " " + contact.lastname
                        : null;
                    let recipient = {
                        label: contact.label,
                        fullname: fullname,
                        firstname: contact.firstname ? contact.firstname : null,
                        lastname: contact.lastname ? contact.lastname : null,
                        email: contact.email,
                        contact_type: "contact",
                        actor_id: contact.id,
                        to_id: toId,
                        toIdArray: ["actor", contact.id, contact.email],
                        oriId: contact.id
                    };
                    if (!this.filterData) {
                        recipient = { ...recipient, ...contact };
                    }
                    this.recipients.push(recipient);
                }
            }
            this.handleChanges(this.recipients);
        },
        recipientLabel(option) {
            if (this.labelWithEmail) {
                return `${option.label} (${option.email})`;
            }

            return option.label;
        },
        handleChanges() {
            this.$emit("input", this.recipients);
        }
    }
};
</script>

<style>
.style-chooser .vs__search {
    width: 100% !important;
}
</style>
